import { FooterActions, FooterButtons, PageFooter } from '@/Components/PageFooter/PageFooter';
import Title from '@/Components/Title/Title';
import { useFilingCabinetContext } from '@/Contexts/Authenticated/FilingCabinetContext';
import { FilingCabinetItem } from '@/Models/FilingCabinet/FilingCabinet';
import LoadingPage from '@/Pages/Helper/LoadingPage/LoadingPage';
import { DRAGON_LANGUAGE, GetDragonLanguageOptions } from '@/RazorTS/Reporting/Domain/DragonLanguage';
import FilingCabinetStore from '@/Stores/FilingCabinetStore';
import UserDefaultsStore from '@/Stores/UserDefaultsStore';
import { DobButton, DobCheckbox, DobInput, DobLabel, DobMultiselect, DobRadioGroup, DobSelect, DobSwitch } from '@dedalusdiit/dob-components';
import { useTheme } from '@mui/material';
import { Editor } from '@tinymce/tinymce-react';
import { observer } from 'mobx-react-lite';
import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import type { Editor as TinyMCEEditor } from 'tinymce';
import * as API from '../../API';
import Styles from './UserDefaults.module.scss';

interface UserDefaultsProps {
    initialState: FilingCabinetItem;
}

enum StatusEnum {
    Planned = 1,
    Arrived = 2,
    Started = 3,
    Completed = 4,
    Dictated = 5,
    Finished = 6,
    Correction = 8,
    Control = 9,
    Cancelled = 10,
    Transcription = 11,
}

const getTranslatedStatusName = (status: StatusEnum, t: (val: string) => string): string => {
    switch (status) {
        case StatusEnum.Planned:
            return t('Planned');
        case StatusEnum.Arrived:
            return t('WaitingRoom');
        case StatusEnum.Started:
            return t('Started');
        case StatusEnum.Completed:
            return t('Completed');
        case StatusEnum.Dictated:
            return t('Dictated');
        case StatusEnum.Finished:
            return t('Finished');
        case StatusEnum.Correction:
            return t('Correction');
        case StatusEnum.Control:
            return t('Control');
        case StatusEnum.Cancelled:
            return t('Cancelled');
        case StatusEnum.Transcription:
            return t('Transcription');
        default: {
            console.error('Unknown ID');
            return '';
        }
    }
};

const UserDefaults = observer(({ initialState }: UserDefaultsProps): React.ReactElement => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { id } = useParams();
    const navigate = useNavigate();

    const fcContext = useFilingCabinetContext();
    const store = useMemo(() => new UserDefaultsStore(new FilingCabinetStore(initialState, fcContext), id), [initialState]);

    const editorRef = useRef<TinyMCEEditor | null>(null);

    const genders = useMemo(() => [
        { label: t('Male'), value: 'M' },
        { label: t('Female'), value: 'F' },
    ], []);

    const dragonLanguages = useMemo(() =>
        Array.from(GetDragonLanguageOptions().entries())
            .map((x: [ DRAGON_LANGUAGE, string ]) => ({ value: x[0] as string, label: x[1] })),
    []);

    const findInternalRadiologist = async (searchTerm: string): Promise<{ value: number; label: string }[]> => {
        return API.radiologists.search(searchTerm)
            .then((res) => {
                return res.map((x) => ({ value: x.Id, label: x.FullName }));
            });
    };

    if (store.state.userModel == undefined || store.state.optionLists == undefined) {
        return <LoadingPage />;
    }

    const filterPlannedStatusInWorklistOptions = [
        { label: t('All'), value: 'All' },
        { label: t('Hospitalized'), value: 'Hospitalized' },
    ];

    return (
        <div id={Styles.userDefaults}>
            <Title titleName={`${t('UserDefaults')} - #${store.state.userModel.User_UID.toString()} - ${store.state.userModel?.FullUserName}`} />
            <div style={{ overflowY: 'auto', flexGrow: '1' }}>
                <details className={Styles.details} open={store.state.view.loginInfoOpen}>
                    <summary onClick={(e) => store.toggleCollapsible(e, 'loginInfoOpen')}>{t('LoginInformation')}</summary>
                    <div className={Styles.gridSpacing} style={{ gridTemplateColumns: 'repeat(3, auto)' }}>
                        <span>
                            <DobInput
                                isRequired
                                direction="vertical"
                                label={t('Name')}
                                value={store.state.userModel.FullUserName}
                                onValueChange={(val) => store.setProperty(store.state.userModel, 'FullUserName', val)}
                                error={store.state.errors?.FullUserName != null}
                            />
                            <div className={Styles.validationError}>{store.state.errors?.FullUserName}</div>
                        </span>
                        <span>
                            <DobInput
                                isRequired
                                direction="vertical"
                                label={t('UserName')}
                                value={store.state.userModel.UserName}
                                onValueChange={(val) => store.setProperty(store.state.userModel, 'UserName', val)}
                                error={store.state.errors?.UserName != null}
                            />
                            <div className={Styles.validationError}>{store.state.errors?.UserName}</div>
                        </span>
                        <span>
                            <DobInput
                                direction="vertical"
                                label={t('Password')}
                                type="password"
                                value={store.state.userModel.NewPassword}
                                onValueChange={(val) => store.setProperty(store.state.userModel, 'NewPassword', val)}
                                error={store.state.errors?.NewPassword != null}
                            />
                            <div className={Styles.validationError}>{store.state.errors?.NewPassword}</div>
                        </span>

                        <DobSelect
                            direction="vertical"
                            label={t('Gender')}
                            options={genders}
                            optionSelected={genders.find((x) => x.value == store.state.userModel.Gender)}
                            onChange={(opt) => opt && store.setProperty(store.state.userModel, 'Gender', opt.value)}
                        />
                        <DobCheckbox
                            label={t('UseADAuthentication')}
                            checked={store.state.userModel.UseADAuthenticationForUser}
                            onCheckedChange={(checked) => store.setProperty(store.state.userModel, 'UseADAuthenticationForUser', checked as boolean)}
                        />
                        <span>
                            <DobInput
                                direction="vertical"
                                label={t('RepeatPassword')}
                                type="password"
                                value={store.state.userModel.NewPassword2}
                                onValueChange={(val) => store.setProperty(store.state.userModel, 'NewPassword2', val)}
                                error={store.state.errors?.NewPassword2 != null}
                            />
                            <div className={Styles.validationError}>{store.state.errors?.NewPassword2}</div>
                        </span>

                        <DobCheckbox
                            label={t('IsUserTemplate')}
                            checked={store.state.userModel.IsUserTemplate}
                            onCheckedChange={(checked) => store.setProperty(store.state.userModel, 'IsUserTemplate', checked as boolean)}
                        />
                        <DobCheckbox
                            label={t('Active')}
                            checked={store.state.userModel.Active}
                            onCheckedChange={(checked) => store.setProperty(store.state.userModel, 'Active', checked as boolean)}
                        />
                    </div>
                </details>

                <details className={Styles.details} open={store.state.view.generalInfoOpen}>
                    <summary onClick={(e) => store.toggleCollapsible(e, 'generalInfoOpen')}>{t('GeneralInformation')}</summary>
                    <div>
                        <div className={Styles.gridSpacing} style={{ gridTemplateColumns: 'repeat(4, auto)' }}>
                            <span>
                                <DobSelect
                                    direction="vertical"
                                    label={t('ShowTimingFromOrderStatus')}
                                    optionLabel="Label"
                                    optionValue="Value"
                                    options={store.state.optionLists?.statuses}
                                    optionSelected={store.state.optionLists?.statuses.find(
                                        (x: { Value: string }) => x.Value == store.state.userModel.StartTimingOnStatus_UID.toString(),
                                    )}
                                    onChange={(option: { Value: number } | null) => option && store.setProperty(store.state.userModel, 'StartTimingOnStatus_UID', option.Value)}
                                    error={store.state.errors?.StartTimingOnStatus_UID != null}
                                />
                                <div className={Styles.validationError}>{store.state.errors?.StartTimingOnStatus_UID}</div>
                            </span>
                            <span>
                                <DobSelect
                                    direction="vertical"
                                    label={t('ShowTimingToOrderStatus')}
                                    optionLabel="Label"
                                    optionValue="Value"
                                    options={store.state.optionLists?.statuses}
                                    optionSelected={store.state.optionLists?.statuses.find(
                                        (x: { Value: string }) => x.Value == store.state.userModel.StopTimingOnStatus_UID.toString(),
                                    )}
                                    onChange={(option: { Value: number } | null) => option && store.setProperty(store.state.userModel, 'StopTimingOnStatus_UID', option.Value)}
                                    error={store.state.errors?.StopTimingOnStatus_UID != null}
                                />
                                <div className={Styles.validationError}>{store.state.errors?.StopTimingOnStatus_UID}</div>
                            </span>
                            <DobInput
                                direction="vertical"
                                label={t('DefaultFilterOrders')}
                                value={store.state.userModel.DefaultFilterOrders}
                                onValueChange={(val) => store.setProperty(store.state.userModel, 'DefaultFilterOrders', val)}
                            />
                            <DobInput
                                direction="vertical"
                                label={t('DefaultFilterReports')}
                                value={store.state.userModel.DefaultFilterReports}
                                onValueChange={(val) => store.setProperty(store.state.userModel, 'DefaultFilterReports', val)}
                            />

                            <div style={{ gridColumn: 'span 2' }}>
                                <DobLabel text={t('StatusesToChoose')} />
                                <div className={Styles.gridSpacingSmall} style={{ gridTemplateColumns: 'repeat(4, auto)' }}>
                                    {store.state.userModel.AvailableStatusToChooseIDs.map((s) => (
                                        <DobCheckbox
                                            key={s}
                                            label={getTranslatedStatusName(s, t)}
                                            checked={store.state.userModel.SelectedStatusToChooseIDs.includes(s)}
                                            onCheckedChange={(checked) => store.setSelectedStatusToChoose(checked, s)}
                                        />
                                    ))}
                                </div>
                            </div>
                            <DobInput
                                direction="vertical"
                                label={t('ClientIDReadSISEID')}
                                value={store.state.userModel.ClientIDReadSISEID}
                                onValueChange={(val) => store.setProperty(store.state.userModel, 'ClientIDReadSISEID', val)}
                            />
                            <div className={Styles.flexSpacing}>
                                <DobCheckbox
                                    label={t('ShowReadEIDButton')}
                                    checked={store.state.userModel.ShowReadEIDButton}
                                    onCheckedChange={(checked) => store.setProperty(store.state.userModel, 'ShowReadEIDButton', checked as boolean)}
                                />
                                <DobCheckbox
                                    label={t('GetReadSISEIDClientByIP')}
                                    checked={store.state.userModel.GetClientByIPForReadSISEID}
                                    onCheckedChange={(checked) => store.setProperty(store.state.userModel, 'GetClientByIPForReadSISEID', checked as boolean)}
                                />
                            </div>

                            <div style={{ gridColumn: 'span 2' }}>
                                <DobLabel text={t('StatusesToChange')} />
                                <div className={Styles.gridSpacingSmall} style={{ gridTemplateColumns: 'repeat(4, auto)' }}>
                                    {store.state.userModel.AvailableStatusToChangeIDs.map((s) => (
                                        <DobCheckbox
                                            key={s}
                                            label={getTranslatedStatusName(s, t)}
                                            checked={store.state.userModel.SelectedStatusToChangeIDs.includes(s)}
                                            onCheckedChange={(checked) => store.setSelectedStatusToChange(checked, s)}
                                        />
                                    ))}
                                </div>
                            </div>
                            <DobSelect
                                direction="vertical"
                                label={t('DisplayLength')}
                                optionLabel="Label"
                                optionValue="Value"
                                options={store.state.optionLists?.displayLengths}
                                optionSelected={store.state.optionLists?.displayLengths.find(
                                    (x: { Value: string }) => x.Value == store.state.userModel.DisplayLength_UID.toString(),
                                )}
                                onChange={(option: { Value: number } | null) => option && store.setProperty(store.state.userModel, 'DisplayLength_UID', option.Value)}
                            />
                            <DobSelect
                                direction="vertical"
                                label={t('DisplayLanguage')}
                                optionLabel="Label"
                                optionValue="Value"
                                options={store.state.optionLists?.displayLanguages}
                                optionSelected={store.state.optionLists?.displayLanguages.find((x: { Value: string }) => x.Value == store.state.userModel.DisplayLanguage)}
                                onChange={(option: { Value: string } | null) => option && store.setProperty(store.state.userModel, 'DisplayLanguage', option.Value)}
                            />

                            <DobCheckbox
                                label={t('RestrictRoomsToCurrentSite')}
                                checked={store.state.userModel.RestrictRoomsToCurrentSite}
                                onCheckedChange={(checked) => store.setProperty(store.state.userModel, 'RestrictRoomsToCurrentSite', checked as boolean)}
                            />
                        </div>
                    </div>
                </details>

                <details className={Styles.details} open={store.state.view.technicianInfoOpen}>
                    <summary onClick={(e) => store.toggleCollapsible(e, 'technicianInfoOpen')}>
                        {t('Technician')} {t('Info')}
                    </summary>
                    <div className={Styles.flexColumns}>
                        <div style={{ width: '50%' }}>
                            <DobLabel text={`${t('WorklistOrders')} ${t('Status')} ${t('ToShow')}`} />
                            <div className={Styles.gridSpacingSmall} style={{ gridTemplateColumns: 'repeat(4, auto)' }}>
                                {store.state.userModel.AvailableStatusIDs.map((s) => {
                                    if (s as StatusEnum == StatusEnum.Planned) {
                                        return (
                                            <div key={s} className={Styles.plannedStatus}>
                                                <DobCheckbox
                                                    key={s}
                                                    label={getTranslatedStatusName(s, t)}
                                                    checked={store.state.userModel?.SelectedStatusIDs.includes(s)}
                                                    onCheckedChange={(checked) => store.setSelectedAvailableStatusIds(checked, s)}
                                                />
                                                <DobSelect
                                                    size="small"
                                                    optionSelected={filterPlannedStatusInWorklistOptions.find((x) => x.value == store.state.userModel?.FilterPlannedStatusInWorklist)}
                                                    options={filterPlannedStatusInWorklistOptions}
                                                    onChange={(opt) => opt && store.setProperty(store.state.userModel, 'FilterPlannedStatusInWorklist', opt.value)}
                                                />
                                            </div>
                                        );
                                    }
                                    return (
                                        <DobCheckbox
                                            key={s}
                                            label={getTranslatedStatusName(s, t)}
                                            checked={store.state.userModel?.SelectedStatusIDs.includes(s)}
                                            onCheckedChange={(checked) => store.setSelectedAvailableStatusIds(checked, s)}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                        <div className={Styles.flexSpacing}>
                            <DobCheckbox
                                label={t('SpecificSearchOnOrderOrDOB')}
                                checked={store.state.userModel.SpecificSearch}
                                onCheckedChange={(checked) => store.setProperty(store.state.userModel, 'SpecificSearch', checked as boolean)}
                            />
                            <DobCheckbox
                                label={t('OpenOrderInEditModeFromWorklist')}
                                checked={store.state.userModel.OpenInEditFromWorklist}
                                onCheckedChange={(checked) => store.setProperty(store.state.userModel, 'OpenInEditFromWorklist', checked as boolean)}
                            />
                        </div>
                    </div>
                </details>

                <details className={Styles.details} open={store.state.view.radiologistInfoOpen}>
                    <summary onClick={(e) => store.toggleCollapsible(e, 'radiologistInfoOpen')}>
                        {t('Radiologist')} {t('Info')}
                    </summary>
                    <div className={Styles.flexColumns}>
                        <div style={{ width: '33%' }}>
                            <DobLabel text={`${t('internalStaff')} ${t('Radiologist')}`} />
                            <div style={{ display: 'flex' }}>
                                <DobSelect
                                    async
                                    clearable
                                    loadOptions={findInternalRadiologist}
                                    optionSelected={store.state.userModel.RadiologistID != null ? { label: store.state.userModel.Radiologist_Text, value: store.state.userModel.RadiologistID } : null}
                                    onChange={(option: { label: string; value: number } | null) => option && store.setInternalRadiologist(option)}
                                />
                                <DobButton
                                    disabled={store.state.userModel.RadiologistID == null}
                                    iconSize="15px"
                                    color="neutral"
                                    icon="box-arrow-right"
                                    onClick={() => navigate(`/InternalStaff/DetailRadiologists/${store.state.userModel.RadiologistID}`)}
                                    title={t('Detail')}
                                />
                                <DobButton
                                    disabled={store.state.userModel.RadiologistID == null}
                                    color="neutral"
                                    icon="x"
                                    onClick={() => store.removeInternalRadiologist()}
                                    title={t('Remove')}
                                />
                            </div>
                        </div>
                        <div className={Styles.flexSpacing} style={{ width: '33%' }}>
                            <DobCheckbox
                                label={t('AssistentRadiologist')}
                                checked={store.state.userModel.AssistentRadiologist}
                                onCheckedChange={(checked) => store.setProperty(store.state.userModel, 'AssistentRadiologist', checked as boolean)}
                            />
                            <DobCheckbox
                                label={t('ShowIndicatorUserRadiologistProcedure')}
                                checked={store.state.userModel.ShowIndicatorUserRadiologistProcedure}
                                onCheckedChange={(checked) => store.setProperty(store.state.userModel, 'ShowIndicatorUserRadiologistProcedure', checked as boolean)}
                            />
                            <DobCheckbox
                                label={t('AutoSkipReportPopup')}
                                checked={store.state.userModel.AutoSkipReportPopup}
                                onCheckedChange={(checked) => store.setProperty(store.state.userModel, 'AutoSkipReportPopup', checked as boolean)}
                            />
                        </div>
                        <div className={Styles.flexSpacing} style={{ width: '33%' }}>
                            <DobSelect
                                direction="vertical"
                                label={t('ShowReportsFrom')}
                                optionLabel="Label"
                                optionValue="Value"
                                options={store.state.optionLists?.availableDatesForShowReportsFrom}
                                optionSelected={store.state.optionLists?.availableDatesForShowReportsFrom.find(
                                    (x: { Value: string }) => x.Value == store.state.userModel.ShowReportsFrom.toString(),
                                )}
                                onChange={(option: { Value: number } | null) => option && store.setProperty(store.state.userModel, 'ShowReportsFrom', option.Value)}
                            />
                            <DobCheckbox
                                label={t('FilterOutDefaultRadiologistFromMyReports')}
                                checked={store.state.userModel.FilterOutDefaultRadiologistFromMyReports}
                                onCheckedChange={(checked) => store.setProperty(store.state.userModel, 'FilterOutDefaultRadiologistFromMyReports', checked as boolean)}
                            />
                        </div>
                    </div>
                </details>

                <details className={Styles.details} open={store.state.view.reportDefaultsOpen}>
                    <summary onClick={(e) => store.toggleCollapsible(e, 'reportDefaultsOpen')}>
                        {t('Report')} {t('Defaults')}
                    </summary>
                    <div className={Styles.flexColumns}>
                        <div>
                            <div>
                                <DobLabel text={t('StatusWhenSaveReport')} />
                                <DobRadioGroup
                                    options={[
                                        { value: '4', label: t('Completed') },
                                        { value: '5', label: t('Dictated') },
                                        { value: '6', label: t('Finished') },
                                        {
                                            value: '11',
                                            label: t('Transcription'),
                                        },
                                    ]}
                                    value={store.state.userModel.SaveReportStatus_UID.toString()}
                                    onChange={(val) => store.setProperty(store.state.userModel, 'SaveReportStatus_UID', Number(val))}
                                />
                            </div>

                            <br />

                            <div>
                                <DobLabel text={t('StatusWhenValidateReport')} />
                                <DobRadioGroup
                                    options={[
                                        { value: '4', label: t('Completed') },
                                        { value: '5', label: t('Dictated') },
                                        { value: '6', label: t('Finished') },
                                        {
                                            value: '11',
                                            label: t('Transcription'),
                                        },
                                    ]}
                                    value={store.state.userModel.ValidateReportStatus_UID.toString()}
                                    onChange={(val) => store.setProperty(store.state.userModel, 'ValidateReportStatus_UID', Number(val))}
                                />
                            </div>

                            <br />

                            <div>
                                <DobLabel text={t('HeaderStyle')} />
                                <div className={Styles.flexSpacing}>
                                    <DobCheckbox
                                        label={t('HeaderBold')}
                                        checked={store.state.userModel.HeaderBold}
                                        onCheckedChange={(checked) => store.setProperty(store.state.userModel, 'HeaderBold', checked as boolean)}
                                    />
                                    <DobCheckbox
                                        label={t('HeaderUnderline')}
                                        checked={store.state.userModel.HeaderUnderline}
                                        onCheckedChange={(checked) => store.setProperty(store.state.userModel, 'HeaderUnderline', checked as boolean)}
                                    />
                                    <DobCheckbox
                                        label={t('HeaderItalic')}
                                        checked={store.state.userModel.HeaderItalic}
                                        onCheckedChange={(checked) => store.setProperty(store.state.userModel, 'HeaderItalic', checked as boolean)}
                                    />
                                    <DobCheckbox
                                        label={t('Uppercase')}
                                        checked={store.state.userModel.HeaderUppercase}
                                        onCheckedChange={(checked) => store.setProperty(store.state.userModel, 'HeaderUppercase', checked as boolean)}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className={Styles.flexSpacing}>
                            <DobCheckbox
                                label={t('AbleToValidateReportUserDefault')}
                                checked={store.state.userModel.AbleToValidateReport}
                                onCheckedChange={(checked) => store.setProperty(store.state.userModel, 'AbleToValidateReport', checked as boolean)}
                            />
                            <DobCheckbox
                                label={t('ShowScannedOrderOnReport')}
                                checked={store.state.userModel.ShowScannedOrderOnReport}
                                onCheckedChange={(checked) => store.setProperty(store.state.userModel, 'ShowScannedOrderOnReport', checked as boolean)}
                            />
                            <DobCheckbox
                                label={t('ShowScannedOrderNextToReport')}
                                checked={store.state.userModel.ShowScannedOrderNextToReport}
                                onCheckedChange={(checked) => store.setProperty(store.state.userModel, 'ShowScannedOrderNextToReport', checked as boolean)}
                            />
                            <DobCheckbox
                                label={t('ShowVerifyNomenclaturesButton')}
                                checked={store.state.userModel.ShowVerifyNomenclaturesButton}
                                onCheckedChange={(checked) => store.setProperty(store.state.userModel, 'ShowVerifyNomenclaturesButton', checked as boolean)}
                            />
                            <DobCheckbox
                                label={t('ShowConfirmationOnReportSave')}
                                checked={store.state.userModel.ShowConfirmationOnReportSave}
                                onCheckedChange={(checked) => store.setProperty(store.state.userModel, 'ShowConfirmationOnReportSave', checked as boolean)}
                            />
                            <DobCheckbox
                                label={t('ShowConfirmationOnReportValidate')}
                                checked={store.state.userModel.ShowConfirmationOnReportValidate}
                                onCheckedChange={(checked) => store.setProperty(store.state.userModel, 'ShowConfirmationOnReportValidate', checked as boolean)}
                            />
                            <DobCheckbox
                                label={t('ConfirmInvoiceOnVerifyNomenclatures')}
                                checked={store.state.userModel.ConfirmInvoiceOnVerifyNomenclatures}
                                onCheckedChange={(checked) => store.setProperty(store.state.userModel, 'ConfirmInvoiceOnVerifyNomenclatures', checked as boolean)}
                            />
                            <DobCheckbox
                                label={t('AdjustInvoiceOnVerifyNomenclatures')}
                                checked={store.state.userModel.AdjustInvoiceOnVerifyNomenclatures}
                                onCheckedChange={(checked) => store.setProperty(store.state.userModel, 'AdjustInvoiceOnVerifyNomenclatures', checked as boolean)}
                            />
                            <DobCheckbox
                                label={t('ShowEchoProceduresFromStarted')}
                                checked={store.state.userModel.ShowEchoFromStarted}
                                onCheckedChange={(checked) => store.setProperty(store.state.userModel, 'ShowEchoFromStarted', checked as boolean)}
                            />
                            <DobCheckbox
                                label={t('DefaultTranscriptionOnly')}
                                checked={store.state.userModel.DefaultTranscriptionOnly}
                                onCheckedChange={(checked) => store.setProperty(store.state.userModel, 'DefaultTranscriptionOnly', checked as boolean)}
                            />
                            <DobCheckbox
                                label={t('ShowDocumentsInReportByDefault')}
                                checked={store.state.userModel.ShowDocumentsInReportByDefault}
                                onCheckedChange={(checked) => store.setProperty(store.state.userModel, 'ShowDocumentsInReportByDefault', checked as boolean)}
                            />
                            <DobCheckbox
                                label={t('ShowToControl')}
                                checked={store.state.userModel.ShowToControl}
                                onCheckedChange={(checked) => store.setProperty(store.state.userModel, 'ShowToControl', checked as boolean)}
                            />

                            <br />
                            <DobLabel text={t('Justification')} />
                            <DobCheckbox
                                label={t('HideOrdersWithoutLinkedDocs')}
                                checked={store.state.userModel.HideOrdersWithoutLinkedDocs}
                                onCheckedChange={(checked) => store.setProperty(store.state.userModel, 'HideOrdersWithoutLinkedDocs', checked as boolean)}
                            />
                            <DobCheckbox
                                label={t('ShowToJustify')}
                                checked={store.state.userModel.ShowToJustify}
                                onCheckedChange={(checked) => store.setProperty(store.state.userModel, 'ShowToJustify', checked as boolean)}
                            />
                            <DobInput
                                direction="vertical"
                                label={t('ShowDaysInFuture')}
                                value={store.state.userModel.ShowDaysInFuture}
                                onValueChange={(val) => store.setShowDaysInFuture(val)}
                            />

                            <br />
                            <br />
                            <DobCheckbox
                                label={t('ShowDiagnosticQuestionInReport')}
                                checked={store.state.userModel.ShowDiagnosticQuestionInReport}
                                onCheckedChange={(checked) => store.setProperty(store.state.userModel, 'ShowDiagnosticQuestionInReport', checked as boolean)}
                            />
                            <Editor
                                onInit={(_evt, editor) => {
                                    editorRef.current = editor;
                                }}
                                onEditorChange={(a) => store.setReportTemplate(a)}
                                value={store.state.userModel.DefaultReportTemplate}
                                init={{
                                    skin_url: theme.palette.mode === 'dark' ? '/tinymce/skins/ui/dobco' : '/tinymce/skins/ui/oxide',
                                    content_css: theme.palette.mode === 'dark' ? '/tinymce/skins/content/dobco/content.css' : '',
                                    height: 200,
                                    menubar: false,
                                    toolbar:
                                        'bold italic underline | headerstyle casechange | doseinformation clinicalinformation diagnosticquestion proceduredescription procedurecode',
                                    entity_encoding: 'raw',
                                    custom_elements: 'ProcedureCodes,ProcedureDescriptions,Dose,Clinical,Diagnostic',
                                    extended_valid_elements: 'b,i,strong,em,ProcedureCodes[*],ProcedureDescriptions[*],*[*]',
                                    valid_children: '+body[ProcedureCodes|ProcedureDescriptions]',
                                    content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
                                    force_br_newlines: true,
                                    forced_root_block: 'div',
                                    formats: {
                                        bold: { inline: 'b' },
                                        italic: { inline: 'i' },
                                        underline: { inline: 'u' },
                                    },
                                    text_patterns: false,
                                    setup: (editor) => {
                                        editor.on('BeforeSetContent', function (e) {
                                            if (e.content) {
                                                e.content = e.content.replace(
                                                    /(<|<\/)(ProcedureCodes|ProcedureDescriptions|Dose information|Clinical info|Diagnostic question)(>)/g,
                                                    '<$2>',
                                                );
                                            }
                                        });

                                        editor.ui.registry.addButton('headerstyle', {
                                            icon: 'typography',
                                            tooltip: t('HeaderStyle'),
                                            onAction: () => {
                                                const bookmark = editor.selection.getBookmark();
                                                editor.execCommand('RemoveFormat');

                                                const headerStyles = [];
                                                if (store.state.userModel.HeaderBold) {
                                                    headerStyles.push('bold');
                                                }
                                                if (store.state.userModel.HeaderUnderline) {
                                                    headerStyles.push('underline');
                                                }
                                                if (store.state.userModel.HeaderItalic) {
                                                    headerStyles.push('italic');
                                                }
                                                if (store.state.userModel.HeaderUppercase) {
                                                    headerStyles.push('uppercase');
                                                }
                                                headerStyles.forEach((s) => {
                                                    if (s === 'uppercase') {
                                                        editor.selection.setContent(editor.selection.getContent().toUpperCase());
                                                    }
                                                    else {
                                                        editor.formatter.apply(s);
                                                    }
                                                });

                                                editor.selection.moveToBookmark(bookmark);
                                            },
                                        });

                                        editor.ui.registry.addButton('procedurecode', {
                                            icon: 'code-sample',
                                            tooltip: t('AddProcedureCodesTooltip'),
                                            onAction: () => {
                                                editor.execCommand('mceInsertContent', false, '&lt;ProcedureCodes&gt;');
                                            },
                                        });

                                        editor.ui.registry.addIcon(
                                            'doseinformation',
                                            `<img style="width: 18px; height: 18px" src="/icons/icnRadioactiveBlack.png" alt={t('DoseInformation')} />`,
                                        );
                                        editor.ui.registry.addButton('doseinformation', {
                                            icon: 'doseinformation',
                                            tooltip: t('AddDoseInformationTooltip'),
                                            onAction: () => {
                                                editor.execCommand('mceInsertContent', false, '&lt;Dose information&gt;');
                                            },
                                        });

                                        editor.ui.registry.addButton('clinicalinformation', {
                                            icon: 'info',
                                            tooltip: t('AddClinicalInformationTooltip'),
                                            onAction: () => {
                                                editor.execCommand('mceInsertContent', false, '&lt;Clinical info&gt;');
                                            },
                                        });

                                        editor.ui.registry.addButton('diagnosticquestion', {
                                            icon: 'help',
                                            tooltip: t('AddDiagnosticQuestionTooltip'),
                                            onAction: () => {
                                                editor.execCommand('mceInsertContent', false, '&lt;Diagnostic question&gt;');
                                            },
                                        });

                                        editor.ui.registry.addButton('proceduredescription', {
                                            icon: 'document-properties',
                                            tooltip: t('AddProcedureDescriptionsTooltip'),
                                            onAction: () => {
                                                editor.execCommand('mceInsertContent', false, '&lt;ProcedureDescriptions&gt;');
                                            },
                                        });
                                    },
                                }}
                            />
                        </div>

                        <div className={Styles.flexSpacing}>
                            <DobInput
                                direction="vertical"
                                label={t('ClientIDPACSIntegration')}
                                value={store.state.userModel.ClientIDPACSIntegration}
                                onValueChange={(val) => store.setProperty(store.state.userModel, 'ClientIDPACSIntegration', val)}
                            />
                            <DobCheckbox
                                label={t('GetPACSIntegrationClientByIP')}
                                checked={store.state.userModel.GetClientByIPForPACSIntegration}
                                onCheckedChange={(checked) => store.setProperty(store.state.userModel, 'GetClientByIPForPACSIntegration', checked as boolean)}
                            />
                            <DobCheckbox
                                label={t('OpenPACSIntegrationUserDefault')}
                                checked={store.state.userModel.OpenPACSIntegration}
                                onCheckedChange={(checked) => store.setProperty(store.state.userModel, 'OpenPACSIntegration', checked as boolean)}
                            />
                            <DobCheckbox
                                label={t('OpenEmptyPACSIntegrationOnCloseReport')}
                                checked={store.state.userModel.OpenEmptyPACSIntegrationOnCloseReport}
                                onCheckedChange={(checked) => store.setProperty(store.state.userModel, 'OpenEmptyPACSIntegrationOnCloseReport', checked as boolean)}
                            />
                            <DobInput
                                direction="vertical"
                                label={t('PACSUsername')}
                                value={store.state.userModel.PACSUsername}
                                onValueChange={(val) => store.setProperty(store.state.userModel, 'PACSUsername', val)}
                            />
                            <DobInput
                                direction="vertical"
                                label={t('PACSPassword')}
                                value={store.state.userModel.PACSPassword}
                                onValueChange={(val) => store.setProperty(store.state.userModel, 'PACSPassword', val)}
                            />
                        </div>

                        <div className={Styles.flexSpacing}>
                            <DobInput
                                direction="vertical"
                                label={t('MENumber')}
                                value={store.state.userModel.ME}
                                onValueChange={(val) => store.setProperty(store.state.userModel, 'ME', val)}
                            />
                            <DobCheckbox
                                label={t('DefaultFilterHistoryReportsModality')}
                                checked={store.state.userModel.DefaultFilterHistoryReportsModality}
                                onCheckedChange={(checked) => store.setProperty(store.state.userModel, 'DefaultFilterHistoryReportsModality', checked as boolean)}
                            />
                            <DobCheckbox
                                label={t('DefaultFilterHistoryReportsProcedures')}
                                checked={store.state.userModel.DefaultFilterHistoryReportsProcedures}
                                onCheckedChange={(checked) => store.setProperty(store.state.userModel, 'DefaultFilterHistoryReportsProcedures', checked as boolean)}
                            />
                            <DobCheckbox
                                label={t('ShowTimelineOnBottomOfReport')}
                                checked={store.state.userModel.ShowTimelineOnBottomOfReport}
                                onCheckedChange={(checked) => store.setProperty(store.state.userModel, 'ShowTimelineOnBottomOfReport', checked as boolean)}
                            />
                            <DobInput
                                direction="vertical"
                                label={t('PACSOrganization')}
                                value={store.state.userModel.PACSOrganization}
                                onValueChange={(val) => store.setProperty(store.state.userModel, 'PACSOrganization', val)}
                            />
                        </div>
                    </div>
                </details>

                <details className={Styles.details} open={store.state.view.speechRecognitionOpen}>
                    <summary onClick={(e) => store.toggleCollapsible(e, 'speechRecognitionOpen')}>{t('SpeechRecognition')}</summary>
                    <div className={Styles.flexColumns}>
                        <div className={Styles.flexSpacing} style={{ width: '33%' }}>
                            <DobCheckbox
                                label={t('HasSpeechRecognition')}
                                checked={store.state.userModel.HasSpeechRecognition}
                                onCheckedChange={(checked) => store.setProperty(store.state.userModel, 'HasSpeechRecognition', checked as boolean)}
                            />
                            <DobCheckbox
                                label={t('StartSpeechRecognitionUserDefault')}
                                checked={store.state.userModel.StartSpeechRecognition}
                                onCheckedChange={(checked) => store.setProperty(store.state.userModel, 'StartSpeechRecognition', checked as boolean)}
                            />
                            <DobInput
                                direction="vertical"
                                label={t('ClientIDSpeechRecognition')}
                                value={store.state.userModel.ClientIDSpeechRecognition}
                                disabled={store.state.userModel?.GetClientByIPForSpeechRecognition}
                                onValueChange={(val) => store.setProperty(store.state.userModel, 'ClientIDSpeechRecognition', val)}
                            />
                            <DobCheckbox
                                label={t('GetSpeechRecognitionClientByIP')}
                                checked={store.state.userModel.GetClientByIPForSpeechRecognition}
                                onCheckedChange={(checked) => store.setProperty(store.state.userModel, 'GetClientByIPForSpeechRecognition', checked as boolean)}
                            />
                            <DobCheckbox
                                label={t('DisableSpeechRecognitionKeyboardShortcuts')}
                                checked={store.state.userModel.DisableSpeechRecognitionKeyboardShortcuts}
                                onCheckedChange={(checked) => store.setProperty(store.state.userModel, 'DisableSpeechRecognitionKeyboardShortcuts', checked as boolean)}
                            />

                            <br />

                            <DobSwitch label={t('SpeechRecognition')} checked={store.state.userModel.Recognition} onChange={(c) => store.setRecognitionOption(c)} />
                            <DobSwitch label={t('Correction')} checked={store.state.userModel.Correction} onChange={(c) => store.setCorrectionOption(c)} />
                        </div>

                        <div style={{ width: '33%' }}>
                            <DobRadioGroup
                                options={store.state.optionLists?.speechRecognitions?.map((x: { Value: string; Label: string }) => ({ value: x.Value, label: x.Label }))}
                                value={store.state.userModel.SpeechRecognition_UID?.toString()}
                                onChange={(val) => store.setProperty(store.state.userModel, 'SpeechRecognition_UID', Number(val))}
                                disabled={!store.state.userModel.HasSpeechRecognition}
                            />
                            <br />
                            <DobSelect
                                direction="vertical"
                                label={t('EOLAction')}
                                optionLabel="Label"
                                optionValue="Value"
                                options={store.state.optionLists?.eolG2}
                                optionSelected={store.state.optionLists?.eolG2.find((x: { Value: string }) => x.Value == store.state.userModel.EOLSaveOrValidate.toString())}
                                onChange={(option: { Value: number } | null) => option && store.setProperty(store.state.userModel, 'EOLSaveOrValidate', option.Value)}
                            />
                        </div>

                        <div className={Styles.flexSpacing} style={{ width: '33%' }}>
                            <DobInput
                                direction="vertical"
                                label={t('SpeechUserName')}
                                value={store.state.userModel.SpeechUsername}
                                onValueChange={(val) => store.setProperty(store.state.userModel, 'SpeechUsername', val)}
                            />
                            <DobInput
                                direction="vertical"
                                label={t('APIToken')}
                                value={store.state.userModel.APIToken}
                                onValueChange={(val) => store.setProperty(store.state.userModel, 'APIToken', val)}
                            />
                            <DobMultiselect
                                direction="vertical"
                                label={'Dragon - ' + t('Language')}
                                placeholder={t('DisplayLanguage')}
                                options={dragonLanguages}
                                optionsSelected={store.state.userModel.DragonLanguages?.map((x) => dragonLanguages.find((y) => y.value == x)).filter((x): x is { value: string; label: string } => x !== undefined)}
                                onChange={(options: { value: string; label: string }[]) => store.setSelectedDragonLanguages(options)}
                            />
                            <DobCheckbox
                                label={t('DragonHoldToRecord')}
                                checked={store.state.userModel.DragonHoldToRecord}
                                onCheckedChange={(checked) => store.setProperty(store.state.userModel, 'DragonHoldToRecord', checked as boolean)}
                            />
                            <DobCheckbox
                                label={t('HideDragonDictationWindow')}
                                checked={store.state.userModel.HideDragonDictationWindow}
                                onCheckedChange={(checked) => store.setProperty(store.state.userModel, 'HideDragonDictationWindow', checked as boolean)}
                            />
                        </div>
                    </div>
                </details>
            </div>

            <PageFooter>
                <FooterActions />
                <FooterButtons>
                    <DobButton onClick={() => store.save()}>{t('SaveAndClose')}</DobButton>
                </FooterButtons>
            </PageFooter>
        </div>
    );
});

export default UserDefaults;
